import { PadSessionStagesV1, VirtualPadAnswerMsg, VirtualPadCloseMsg, VirtualPadCreatedMsg, VirtualPadCreateMsg, VirtualPadErrorMsg, VirtualPadMessageTypes, VirtualPadOfferMsg, VirtualPadPeerJoinedMsg, VirtualPadPeerLeftMsg, VirtualPadPingMsg, VirtualPadPongMsg, VirtualPadReceiveICECandidatesMsg, VirtualPadSendICECandidatesMsg, VirtualPadSessionResMessages } from "@/services/VirtualPad/v1/virtualPad_v1_types"
import { VPServerConnection } from "../VPServerConnection";
import { ISessionProtocol } from "../VPSessionProtocolStrategy";
import { usePadsStore } from "@/stores/usePadsStore";
import { PadSessionStage, VirtualPadRoles } from "@/interfaces/virtualPad_types";
import { PadManager } from "../PadManager";

export class SessionProtocol implements ISessionProtocol {
  private static readonly SESSION_CREATION_TIMEOUT_SECONDS = 2

  private stage: PadSessionStagesV1 = PadSessionStagesV1.NONE
  private sessionID: string | null = null
  private hostConnectionID: string | null = null
  private readonly padManager: PadManager

  private sessionCreatedPromise: Promise<void> | null = null
  private resolveSessionCreated: (() => void) | null = null
  private rejectSessionCreated: ((reason?: unknown) => void) | null = null
  private sessionCreationTimeout: number | null = null

  /**
   * Initialize SessionProtocol class
   */
  constructor () {
    this.padManager = PadManager.getInstance()
  }

  public whoami (): string {
    return 'Session protocol v1'
  }

  /**
   * Session creation protocol
   */
  public create (): void {
    if (this.sessionID !== null) {
      throw new Error('VIRTUAL PAD :: CREATE SESSION :: A session already exists')
    }

    const msg: VirtualPadCreateMsg = {
      type: VirtualPadMessageTypes.CREATE,
      role: VirtualPadRoles.HOST
    }
    VPServerConnection.getInstance().sendMessage(msg)
    this.setStage(PadSessionStagesV1.CONNECTED)
  }

  /**
   * Waits until the session has been created by the server
   *
   * If the session is already created, resolves immediately.
   * If a creation wait is already in progress, returns the same promise.
   */
  public waitUntilCreated (): Promise<void> {
    if (this.stage === PadSessionStagesV1.CREATED) {
      return Promise.resolve()
    }

    if (this.sessionCreatedPromise !== null) {
      return this.sessionCreatedPromise
    }

    this.sessionCreatedPromise = new Promise<void>((resolve, reject) => {
      this.resolveSessionCreated = resolve
      this.rejectSessionCreated = reject

      this.sessionCreationTimeout = window.setTimeout(() => {
        this.rejectSessionCreation(new Error('VIRTUAL PAD :: CREATE SESSION :: Session creation timeout'))
      }, 1000 * SessionProtocol.SESSION_CREATION_TIMEOUT_SECONDS)
    })

    return this.sessionCreatedPromise
  }

  /**
   * Resolves the pending session creation wait
   */
  private resolveSessionCreation (): void {
    if (this.resolveSessionCreated === null) {
      return
    }

    const resolve = this.resolveSessionCreated

    this.clearSessionCreationWait()
    resolve()
  }

  /**
   * Rejects the pending session creation wait
   * @param error Session creation error
   */
  private rejectSessionCreation (error: unknown): void {
    if (this.rejectSessionCreated === null) {
      return
    }

    const reject = this.rejectSessionCreated

    this.clearSessionCreationWait()
    reject(error)
  }

  /**
   * Clears the current session creation wait
   */
  private clearSessionCreationWait (): void {
    if (this.sessionCreationTimeout !== null) {
      window.clearTimeout(this.sessionCreationTimeout)
      this.sessionCreationTimeout = null
    }

    this.sessionCreatedPromise = null
    this.resolveSessionCreated = null
    this.rejectSessionCreated = null
  }

  /**
   * Session closure protocol
   */
  public close (): void {
    if (this.sessionID === null)
      throw new Error(`VIRTUAL PAD :: CLOSE SESSION :: Session doesn't exist`)

    const msg: VirtualPadCloseMsg = {
      type: VirtualPadMessageTypes.CLOSE
    }
    VPServerConnection.getInstance().sendMessage(msg)
    this.setStage(PadSessionStagesV1.CLOSED)
  }

  /**
   * Sends a WebRTC offer to the specified virtual pad through the signaling server
   * 
   * @param connectionId Connection identifier of the destination virtual pad
   * @param sdp Local offer SDP
   * @throws If the current session doesn't exist
   */
  public sendOffer (connectionId: string, sdp: string) {
    if (this.sessionID === null)
      throw new Error(`VIRTUAL PAD :: SEND OFFER :: Session doesn't exist. ConnectionId: ${connectionId}`)

    const msg: VirtualPadOfferMsg = {
      type: VirtualPadMessageTypes.OFFER,
      target: connectionId,
      sdp: sdp
    }
    VPServerConnection.getInstance().sendMessage(msg)
  }

  /**
   * Sends a local ICE candidate to the specified virtual pad through the signaling server
   * 
   * @param connectionId Connection identifier of the destination virtual pad
   * @param candidate Local ICE candidate
   * @throws If the current session doesn't exist
   */
  public sendIceCandidate (connectionId: string, candidate: RTCIceCandidateInit) {
    if (this.sessionID === null)
      throw new Error(`VIRTUAL PAD :: SEND ICE CANDIDATE :: Session doesn't exist. ConnectionId: ${connectionId}`)

    const msg: VirtualPadSendICECandidatesMsg = {
      type: VirtualPadMessageTypes.ICE_CANDIDATE,
      target: connectionId,
      candidate: candidate 
    }
    VPServerConnection.getInstance().sendMessage(msg)
  }

  /**
   * Resets the session protocol state
   * 
   * This method only clears protocol-specific state. Closing virtual
   * pad connections is the responsibility of PadSessionManager
   */
  public reset (): void {
    this.rejectSessionCreation(
      new Error('VIRTUAL PAD :: CREATE SESSION :: Session creation cancelled')
    )

    this.stage = PadSessionStagesV1.NONE
    this.sessionID = null
    this.hostConnectionID = null
    usePadsStore.getState().resetVPadSession()
  }

  /**
   * Handle messages received from the server
   * @param e Message event received via WebSockets
   */
  public handleMessage (e: MessageEvent) {
    const data = JSON.parse(e.data) as VirtualPadSessionResMessages

    switch(data.type) {
      case VirtualPadMessageTypes.CREATED: {
        this.handleCreatedSession(data)
        break
      }

      case VirtualPadMessageTypes.PEER_JOINED: {
        this.handlePeerJoined(data)
        break
      }

      case VirtualPadMessageTypes.PEER_LEFT: {
        this.handlePeerLeft(data)
        break
      }

      case VirtualPadMessageTypes.ANSWER: {
        this.handleAnswer(data)
        break
      }

      case VirtualPadMessageTypes.ICE_CANDIDATE: {
        this.handleIceCandidate(data)
        break
      }

      case VirtualPadMessageTypes.PING: {
        this.handlePing(data)
        break
      }

      case VirtualPadMessageTypes.ERROR: {
        this.handleError(data)
        break
      }

      default: {
        console.warn('SESSION PROTOCOL v1 :: Unknow message', data)
        break
      }
    }
  }

  /**
   * Handle session creation
   * @param data Session creation data
   */
  private handleCreatedSession (data: VirtualPadCreatedMsg): void {
    this.sessionID = data.sessionID
    this.hostConnectionID = data.connectionID
    usePadsStore.getState().setVpSessionId(data.sessionID)
    this.setStage(PadSessionStagesV1.CREATED)
    this.resolveSessionCreation()
  }

  /**
   * Handles a virtual pad joining the current session
   *
   * @param data Virtual pad peer joined message
   */
  private handlePeerJoined(data: VirtualPadPeerJoinedMsg): void {
    void this.padManager
      .addVPad(data.sessionID, data.connectionID)
      .catch(error => {
        if (error instanceof Error && error.name === 'maxGamepadsReached') {
          console.warn(`SESSION PROTOCOL v1 :: PEER JOINED :: Couldn't add virtual pad.`, {
            cause: error
          })
          // TODO: If the maximum number of gamepads was reached, send 'leave' to device?
        } else {
          console.error(`SESSION PROTOCOL v1 :: PEER JOINED :: Couldn't add virtual pad.`, {
            cause: error
          })
        }
      })
  }

  /**
   * Handles a virtual pad leaving the current session
   *
   * @param data Virtual pad peer left message
   */
  private handlePeerLeft(data: VirtualPadPeerLeftMsg): void {
    this.padManager.removeVPad(data.connectionID)
  }

  /**
   * Handles the WebRTC answer received from a virtual pad and forwards
   * it to the corresponding peer connection
   * 
   * @param data Virtual pad answer message
   */
  private handleAnswer(data: VirtualPadAnswerMsg): void {
    if (this.sessionID === null || this.sessionID !== data.sessionID) {
      console.error(`SESSION PROTOCOL v1 :: ANSWER :: Session doesn't exist`)
      return
    }
    
    void this.padManager
      .setVPadAnswer(data.from, data.sdp)
      .catch(error => {
        console.error(`SESSION PROTOCOL v1 :: ANSWER :: Could not apply virtual pad answer`, error)
      })
  }

  /**
   * Handles an ICE candidate received from a virtual pad and forwards
   * it to the corresponding peer connection
   *
   * @param data Virtual pad ICE candidate message
   */
  private handleIceCandidate(data: VirtualPadReceiveICECandidatesMsg): void {
    if (this.sessionID === null || this.sessionID !== data.sessionID) {
      console.error(`SESSION PROTOCOL v1 :: RECEIVE ICE CANDIDATE :: Session doesn't exist`)
      return
    }

    void this.padManager
      .addVPadIceCandidate(
        data.from,
        data.candidate
      )
      .catch(error => {
        console.error(`SESSION PROTOCOL v1 :: RECEIVE ICE CANDIDATE :: Could not add remote ICE candidate`, error)
      })
  }

  /**
   * Handles a ping from the server
   *
   * @param data Virtual pad ping message
   */
  private handlePing(data: VirtualPadPingMsg): void {
    if (this.sessionID === null) return

    if (this.sessionID !== data.sessionID) {
      console.error(`SESSION PROTOCOL v1 :: RECEIVE PING :: Session doesn't exist`)
      return
    }

    const msg: VirtualPadPongMsg = {
      type: VirtualPadMessageTypes.PONG,
    }
    VPServerConnection.getInstance().sendMessage(msg)
  }

  /**
   * Handles a session protocol error
   * @param data Error response received from the server
   */
  private handleError (data: VirtualPadErrorMsg): void {
    console.error('Session protocol error:', data.error)

    this.rejectSessionCreation(
      new Error(`VIRTUAL PAD :: SESSION PROTOCOL :: ${data.error}`)
    )
  }

  /**
   * Get the current sessionID
   * @returns Current sessionID
   */
  public getSessionId (): string | null {
    return this.sessionID
  }

  /**
   * Get the current hostConnectionID
   * @returns Current hostConnectionID
   */
  public getHostConnectedId (): string | null {
    return this.hostConnectionID
  }

  /**
   * Get the current protocol stage
   * @returns Current session stage
   */
  public getStage (): PadSessionStage {
    return this.mapStage(this.stage)
  }

  /**
   * Set the current protocol stage and synchronizes it with the application store
   * @param newStage New protocol stage 
   */
  private setStage (newStage: PadSessionStagesV1) {
    this.stage = newStage
    usePadsStore.getState().setStage(
      this.mapStage(newStage)
    )
  }

  /**
   * Maps the protocol-specific stage to the common application stage
   * 
   * @param stage Protocol stage
   * @returns Common application stage
   */
  private mapStage(stage: PadSessionStagesV1): PadSessionStage {
    switch (stage) {
      case PadSessionStagesV1.NONE:
        return PadSessionStage.NONE

      case PadSessionStagesV1.CONNECTED:
        return PadSessionStage.CONNECTED

      case PadSessionStagesV1.CREATED:
        return PadSessionStage.CREATED

      case PadSessionStagesV1.CLOSED:
        return PadSessionStage.CLOSED
    }
  }
}